import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AdminLayout } from "@/components/Layouts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Edit, Trash2, Search, Check, Ban, KeyRound, LogOut, Upload, FileDown, Copy } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Pagination } from "@/components/Pagination";

const EMPTY = {
  email: "", name: "", phone: "", batch: "", batches: [],
  course: "Beginner Stock Investment Class",
  access_status: "active", access_expiry: "2026-12-31",
};

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-MY", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function AdminStudents() {
  const [params] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(params.get("filter") === "pending" ? "pending" : "all");
  const [batchFilter, setBatchFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  // Bulk selection
  const [selected, setSelected] = useState(new Set());
  const fileInputRef = useRef(null);
  const [importSummary, setImportSummary] = useState(null);

  // Pagination (client-side over the filtered list)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const load = async () => {
    const [s, b] = await Promise.all([
      api.get("/admin/students").then((r) => r.data),
      api.get("/admin/batches").then((r) => r.data),
    ]);
    setStudents(s);
    setBatches(b);
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (st) => {
    setEditing(st);
    // Normalise: prefer server-side `batches` list; fall back to singular legacy `batch`
    const normalizedBatches = Array.isArray(st.batches) && st.batches.length > 0
      ? st.batches
      : (st.batch ? [st.batch] : []);
    setForm({ ...EMPTY, ...st, batches: normalizedBatches });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/admin/students/${editing.id}`, {
          name: form.name, phone: form.phone,
          batches: form.batches || [],
          course: form.course,
          access_status: form.access_status, access_expiry: form.access_expiry,
        });
        toast.success("Student updated");
      } else {
        await api.post("/admin/students", {
          ...form,
          batches: form.batches || [],
          batch: (form.batches && form.batches[0]) || "",
          password: undefined,
        });
        toast.success("Student added. Click 'Send Setup Link' to send the password setup link.");
      }
      setOpen(false);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to save student";
      toast.error(typeof msg === "string" ? msg : "Failed to save student");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this student? This action cannot be undone.")) return;
    try { await api.delete(`/admin/students/${id}`); toast.success("Student deleted"); load(); }
    catch { toast.error("Failed to delete."); }
  };

  const approve = async (id) => {
    try { await api.post(`/admin/students/${id}/approve`); toast.success("Access approved"); load(); }
    catch { toast.error("Failed to approve."); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Close this student's access?")) return;
    try { await api.post(`/admin/students/${id}/revoke`); toast.success("Access closed"); load(); }
    catch { toast.error("Failed to close access"); }
  };

  const sendSetupLink = async (id, alreadySetup) => {
    if (alreadySetup && !window.confirm("This student already has a password. Send a new setup link? They can use it to reset their password.")) return;
    try {
      const { data } = await api.post(`/admin/students/${id}/send-setup-link`);
      // Only show success when backend confirms Resend accepted the email.
      // The backend returns a `resend_id` ONLY when Resend returned a real id.
      if (data?.resend_id) {
        toast.success(`Setup link sent to ${data.email}.`, {
          description: data.warning || `Resend ID: ${data.resend_id}`,
          duration: 6000,
        });
      } else {
        toast.success("Password setup link has been sent.");
      }
    } catch (e) {
      // Surface the real error from the backend (Resend's actual rejection
      // text, e.g. "Domain is not verified", "Invalid API key", etc.).
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" && detail
        ? detail
        : (status === 503
          ? "Email service is not configured. Please add RESEND_API_KEY."
          : "Failed to send setup link.");
      toast.error("Email failed to send", { description: msg, duration: 9000 });
    }
  };

  const forceLogout = async (id) => {
    try {
      await api.post(`/admin/students/${id}/force-logout`);
      toast.success("Student has been logged out from all active sessions.");
      load();
    } catch { toast.error("Failed to force-logout."); }
  };

  // Bulk actions
  const allChecked = filtered().length > 0 && filtered().every((s) => selected.has(s.id));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      const fids = filtered().map((s) => s.id);
      const all = fids.every((id) => next.has(id));
      if (all) fids.forEach((id) => next.delete(id));
      else fids.forEach((id) => next.add(id));
      return next;
    });
  };
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const bulkSendSetup = async () => {
    if (selected.size === 0) return toast.message("No students selected.");
    if (!window.confirm(`Send setup link to ${selected.size} students?`)) return;
    try {
      const { data } = await api.post("/admin/students/bulk/send-setup-link", { ids: Array.from(selected) });
      // The backend now returns explicit per-student outcomes. Show real
      // counts instead of the misleading "all sent" toast.
      if (data.failed > 0) {
        toast.error(`Sent ${data.sent} · Failed ${data.failed} · Skipped ${data.skipped}`, {
          description: data.last_error ? `First error: ${data.last_error}` : "See backend logs for details.",
          duration: 9000,
        });
      } else {
        toast.success(`Sent ${data.sent} · Skipped ${data.skipped}`, {
          description: data.sent > 0 ? "All emails were accepted by Resend." : "Nothing was sent.",
          duration: 6000,
        });
      }
      setSelected(new Set());
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error("Bulk send failed", { description: typeof detail === "string" ? detail : "See backend logs.", duration: 9000 });
    }
  };

  const bulkForceLogout = async () => {
    if (selected.size === 0) return toast.message("No students selected.");
    if (!window.confirm(`Force-logout ${selected.size} students?`)) return;
    try {
      const { data } = await api.post("/admin/students/bulk/force-logout", { ids: Array.from(selected) });
      toast.success(`${data.logged_out} students logged out from all sessions.`);
      load();
      setSelected(new Set());
    } catch { toast.error("Failed to bulk force-logout."); }
  };

  // Import CSV/XLSX
  const downloadTemplate = async () => {
    try {
      const resp = await api.get("/admin/students/template.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "student-access-template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download template."); }
  };

  const onPickImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = [".csv", ".xlsx", ".xls"];
    if (!allowed.some((ext) => file.name.toLowerCase().endsWith(ext))) {
      toast.error("Invalid file format. Please upload an Excel or CSV file only.");
      e.target.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/students/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportSummary(data);
      toast.success("Student list has been updated successfully.");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Import failed.";
      toast.error(typeof msg === "string" ? msg : "Import failed.");
    } finally {
      e.target.value = "";
    }
  };

  function filtered() {
    return students.filter((s) => {
      if (search && !`${s.name} ${s.email} ${s.phone || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter !== "all" && s.access_status !== statusFilter) return false;
      if (batchFilter !== "all" && s.batch !== batchFilter) return false;
      return true;
    });
  }
  const list = filtered();
  // Reset to page 1 whenever any filter changes the visible total
  useEffect(() => { setPage(1); }, [search, statusFilter, batchFilter, pageSize]);
  const safePage = Math.min(page, Math.max(1, Math.ceil(list.length / pageSize)));
  const pagedList = list.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <AdminLayout title="Student Access">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Student Access</h1>
          <p className="mt-1 text-muted-foreground max-w-2xl text-sm leading-relaxed">
            Upload student emails using Excel/CSV. Students can create their password directly from
            the Create Password page using their registered email address. Only emails with status
            <strong> active</strong> can create a password and sign in. The system allows only one
            active session/device per email at a time.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
            <FileDown className="h-4 w-4" /> Sample Template
          </Button>
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="upload-students-btn">
            <Upload className="h-4 w-4" /> Upload CSV/Excel
          </Button>
          <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onPickImport} />
          <Button onClick={openAdd} className="btn-glow" data-testid="add-student-btn">
            <Plus className="h-4 w-4" /> Add Student
          </Button>
        </div>
      </div>

      {/* Shared Create Password link panel — admin shares this with students */}
      <Card className="glass-card mb-4" data-testid="create-pw-link-card">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Create Password Link</p>
            <p className="mt-1 text-sm font-mono text-foreground break-all" data-testid="create-pw-link-url">
              {window.location.origin}/create-password
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Share this link with imported students. They enter their registered email + new password to set up their account directly — no email link needed.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              const url = `${window.location.origin}/create-password`;
              navigator.clipboard?.writeText(url).then(
                () => toast.success("Link copied to clipboard."),
                () => toast.error("Could not copy. Please copy manually."),
              );
            }}
            data-testid="copy-create-pw-link-btn"
          >
            <Copy className="h-4 w-4" /> Copy Create Password Link
          </Button>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, or phone..." className="pl-9" data-testid="student-search" />
        </div>
        <Select value={batchFilter} onValueChange={setBatchFilter}>
          <SelectTrigger className="w-[160px]" data-testid="filter-batch"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Batches</SelectItem>
            {batches.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2" data-testid="bulk-bar">
          <span className="text-xs">{selected.size} dipilih</span>
          <Button size="sm" variant="outline" onClick={bulkSendSetup} data-testid="bulk-send-setup">
            <KeyRound className="h-3.5 w-3.5" /> Send Setup Link
          </Button>
          <Button size="sm" variant="outline" onClick={bulkForceLogout} data-testid="bulk-force-logout">
            <LogOut className="h-3.5 w-3.5" /> Force Logout
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Buang pilihan</Button>
        </div>
      )}

      <Card className="glass-card">
        <CardContent className="p-0">
          <div className="table-wrap">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]"><Checkbox checked={allChecked} onCheckedChange={toggleAll} aria-label="Select all" /></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Batch</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Setup</TableHead>
                  <TableHead>Active Session</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.length === 0 ? (
                  <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-8">No students.</TableCell></TableRow>
                ) : pagedList.map((s) => (
                  <TableRow key={s.id} data-testid={`student-row-${s.id}`}>
                    <TableCell><Checkbox checked={selected.has(s.id)} onCheckedChange={() => toggleOne(s.id)} aria-label={`Select ${s.email}`} /></TableCell>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="text-muted-foreground">{s.email}</TableCell>
                    <TableCell>{s.phone || "—"}</TableCell>
                    <TableCell>{s.batch || "—"}</TableCell>
                    <TableCell><StatusBadge status={s.access_status} /></TableCell>
                    <TableCell>
                      <span className={`text-[11px] inline-flex items-center px-1.5 py-0.5 rounded ${s.account_setup_status === "password_created" ? "bg-teal/10 text-teal" : "bg-muted/40 text-muted-foreground"}`}>
                        {s.account_setup_status === "password_created" ? "Password Created" : "Not Set Up"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`text-[11px] inline-flex items-center px-1.5 py-0.5 rounded ${s.has_active_session ? "bg-teal/10 text-teal" : "bg-muted/40 text-muted-foreground"}`}>
                        {s.has_active_session ? "Active" : "—"}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(s.last_login_at)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {s.access_status === "pending" && (
                        <Button size="sm" variant="ghost" onClick={() => approve(s.id)} title="Approve access" data-testid={`approve-student-${s.id}`}>
                          <Check className="h-3.5 w-3.5 text-teal" />
                        </Button>
                      )}
                      {s.access_status === "active" && (
                        <Button size="sm" variant="ghost" onClick={() => revoke(s.id)} title="Close access" data-testid={`revoke-student-${s.id}`}>
                          <Ban className="h-3.5 w-3.5 text-gold" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => sendSetupLink(s.id, s.account_setup_status === "password_created")} title="Send setup link" data-testid={`send-setup-${s.id}`}>
                        <KeyRound className="h-3.5 w-3.5 text-primary" />
                      </Button>
                      {s.has_active_session && (
                        <Button size="sm" variant="ghost" onClick={() => forceLogout(s.id)} title="Force logout" data-testid={`force-logout-${s.id}`}>
                          <LogOut className="h-3.5 w-3.5 text-amber-500" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-student-${s.id}`}><Edit className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(s.id)} data-testid={`delete-student-${s.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Pagination
            page={safePage}
            pageSize={pageSize}
            total={list.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            testId="students-pagination"
          />
        </CardContent>
      </Card>

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="student-form-modal">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Student" : "Add Student"}</DialogTitle>
            {!editing && (
              <DialogDescription>The new student will receive a setup link via email after you click &quot;Send Setup Link&quot;. You do not need to set a password for students manually.</DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} data-testid="form-student-email" /></div>
              <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-student-name" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="col-span-2">
                <Label>Batches</Label>
                <div className="rounded-lg border border-border/60 bg-muted/20 p-3 max-h-48 overflow-y-auto">
                  {batches.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No batches available. Create one under Batches first.</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {batches.filter(b => (b.status || "active") !== "archived").map((b) => {
                        const checked = (form.batches || []).includes(b.name);
                        return (
                          <label
                            key={b.id}
                            className={`flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer transition-all ${
                              checked ? "border-primary/50 bg-primary/8" : "border-border/60 hover:border-border bg-background/40"
                            }`}
                            data-testid={`student-batch-${b.name.toLowerCase().replace(/\s+/g,'-')}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const list = form.batches || [];
                                const next = e.target.checked
                                  ? Array.from(new Set([...list, b.name]))
                                  : list.filter(x => x !== b.name);
                                setForm({ ...form, batches: next, batch: next[0] || "" });
                              }}
                              className="h-4 w-4 rounded border-border accent-primary"
                            />
                            <span className="text-sm font-medium">{b.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Student inherits access to content restricted to any of the selected batches.
                </p>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.access_status} onValueChange={(v) => setForm({ ...form, access_status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Expiry</Label><Input type="date" value={form.access_expiry} onChange={(e) => setForm({ ...form, access_expiry: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-student-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import summary */}
      <Dialog open={!!importSummary} onOpenChange={(o) => !o && setImportSummary(null)}>
        <DialogContent className="max-w-lg" data-testid="import-summary-modal">
          <DialogHeader>
            <DialogTitle>Import Summary</DialogTitle>
          </DialogHeader>
          {importSummary && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-md border border-border p-2"><div className="text-xs text-muted-foreground">Jumlah baris</div><div className="text-lg font-semibold">{importSummary.total_rows}</div></div>
                <div className="rounded-md border border-teal/30 bg-teal/5 p-2"><div className="text-xs text-muted-foreground">Successfully import</div><div className="text-lg font-semibold text-teal">{importSummary.imported}</div></div>
                <div className="rounded-md border border-primary/30 bg-primary/5 p-2"><div className="text-xs text-muted-foreground">Updated</div><div className="text-lg font-semibold text-primary">{importSummary.updated}</div></div>
                <div className="rounded-md border border-border p-2"><div className="text-xs text-muted-foreground">Duplicates skipped</div><div className="text-lg font-semibold">{importSummary.skipped_duplicates}</div></div>
              </div>
              {importSummary.invalid?.length > 0 && (
                <div>
                  <p className="text-xs font-medium mb-1">Invalid rows ({importSummary.invalid.length})</p>
                  <div className="max-h-48 overflow-y-auto rounded-md border border-destructive/30 bg-destructive/5 text-[11px]">
                    <table className="w-full">
                      <thead><tr className="text-muted-foreground"><th className="text-left p-1.5">Baris</th><th className="text-left p-1.5">Email</th><th className="text-left p-1.5">Sebab</th></tr></thead>
                      <tbody>{importSummary.invalid.slice(0, 50).map((r, i) => (
                        <tr key={i}><td className="p-1.5">{r.row}</td><td className="p-1.5">{r.email}</td><td className="p-1.5">{r.reason}</td></tr>
                      ))}</tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setImportSummary(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
